import { Deserializer } from 'jsonapi-serializer';
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from '../constants/response-status-codes';
import { genericError } from '../models/GenericError';
import { ListQuery } from '../models/ListQuery';
import { Name } from '../models/Name';
import { PaginationModel } from '../models/Pagination';
import { DeleteEntityResult } from '../models/services/DeleteEntityResult';
import { StoreEntityResult } from '../models/services/StoreEntityResult';
import { TimeTriggeredAction } from '../models/TimeTriggeredAction';
import { TimeTriggeredActionWrapper } from '../models/wrappers/TimeTriggeredActionWrapper';
import { APIConnector } from './api/APIConnector';
import { createPagination } from './utilities/paginationBuilder';
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';

const apiConnector = new APIConnector();

export const getTimeTriggeredActions = async (listParameters?: ListQuery): Promise<TimeTriggeredActionWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const fieldsTimeTriggeredAction = '&fields[timeTriggeredAction]=taName,timePattern,devAction';
        const fieldsTimePattern = '&fields[timePattern]=tpName';
        const fieldsDevAction = '&fields[devAction]=deviceImprox,deviceAction,setting';
        const fieldsDeviceAction = '&fields[deviceAction]=actionType';
        const fieldsActionType = '&fields[actionType]=trnName';
        const fieldsSetting = '&fields[setting]=trnName';
        const fieldsDeviceImprox = '&fields[deviceImprox]=devName,site';
        const fieldsSite = '&fields[site]=siteName';

        const url = 'timeTriggeredAction?include=timePattern,devAction.deviceAction.actionType,devAction.deviceImprox.site,devAction.setting'
            + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter) 
            + fieldsTimeTriggeredAction + fieldsTimePattern + fieldsDevAction + fieldsDeviceAction + fieldsActionType + fieldsSetting + fieldsDeviceImprox + fieldsSite;
        
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {

            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail), success: false })
            }

            new Deserializer().deserialize(json, (err: any, response: any[]) => {
                if (err) {
                    return reject(err);
                }

                const timeTriggeredActions: TimeTriggeredAction[] = response.map((timeTriggeredAction): TimeTriggeredAction => {
                    return {
                        id: timeTriggeredAction['id'],
                        name: timeTriggeredAction['ta-name'],
                        timePattern: timeTriggeredAction['time-pattern']['tp-name'],
                        device: timeTriggeredAction['dev-action']['device-improx']['dev-name'],
                        actionType: timeTriggeredAction['dev-action']['device-action']['action-type']['trn-name'],
                        actionName: timeTriggeredAction['dev-action']['setting']['trn-name'],
                        site: timeTriggeredAction['dev-action']['device-improx']['site']['site-name'],
                    }
                });

                return resolve({ rows: timeTriggeredActions, pagination });
            });

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }

}

export const getTimeTriggeredActionById = async (id: string): Promise<TimeTriggeredAction> => {
    try {
        const timeTriggeredActionFields = 'fields[timeTriggeredAction]=taName,timePattern,devAction';
        const timePatternFields = 'fields[timePattern]=tpName';
        const devActionFields = 'fields[devAction]=deviceImprox,deviceAction,setting';
        const deviceImproxFields = 'fields[deviceImprox]=devName,site';
        const deviceActionFields = 'fields[deviceAction]=actionType';
        const actionTypeFields = 'fields[actionType]=trnName';
        const settingFields = 'fields[setting]=trnName';
        const siteFields = 'fields[site]=siteName';
        const url = `timeTriggeredAction/${id}?include=timePattern,devAction.deviceAction.actionType,devAction.deviceImprox.site,devAction.setting&${timeTriggeredActionFields}&${timePatternFields}&${devActionFields}&${deviceImproxFields}&${deviceActionFields}&${actionTypeFields}&${settingFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail), success: false })
            }

            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const timeTriggeredAction: TimeTriggeredAction = {
                    id: data['id'],
                    name: data['ta-name'],
                    timePattern: data['time-pattern']['tp-name'],
                    timePatternId: data['time-pattern']['id'],
                    device: data['dev-action']['device-improx']['dev-name'],
                    deviceId: data['dev-action']['device-improx']['id'],
                    actionType: data['dev-action']['device-action']['action-type']['trn-name'],
                    actionName: data['dev-action']['setting']['trn-name'],
                    site: data['dev-action']['device-improx']['site']['site-name'],
                    deviceActionId: data['dev-action']['id'],
                    deviceAction: data['dev-action']['setting']['trn-name']
                }
                return resolve(timeTriggeredAction);
            });

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }

}

export const getTimeTriggeredActionNameWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const url = `timeTriggeredAction?fields[timeTriggeredAction]=taName${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {

            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail), success: false })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });;
                }

                const timeTriggeredActionNames: Name[] = data.map(timeTriggeredAction => { return { label: timeTriggeredAction['ta-name'], value: timeTriggeredAction['id'] } });
                return resolve({ rows: timeTriggeredActionNames, pagination: newPagination });
            });

        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const storeTimeTriggeredAction = async (timeTriggeredAction: TimeTriggeredAction, createTimeTriggeredAction = true): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            data: {
                type: 'timeTriggeredAction',
                id: timeTriggeredAction.id,
                attributes: {
                    taName: timeTriggeredAction.name
                },
                relationships: {
                    devAction: {
                        data: {
                            type: "devAction",
                            id: timeTriggeredAction.deviceActionId
                        }
                    },
                    timePattern: {
                        data: {
                            type: "timePattern",
                            id: timeTriggeredAction.timePatternId
                        }
                    }
                }
            }
        }

        const url = `timeTriggeredAction${createTimeTriggeredAction ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createTimeTriggeredAction, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createTimeTriggeredAction) {
                    const content = await response.json();

                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }

                    new Deserializer().deserialize(content, (error, newTimeTriggeredAction) => {
                        if (error) {
                            return reject({status: response.status, id: newTimeTriggeredAction.id, success: false });
                        }
                        return resolve({ id: newTimeTriggeredAction.id, success: true });
                    })

                    return;
                }

                if (response.status === NO_CONTENT) {
                    return resolve({ id: timeTriggeredAction.id, success: true })
                }
                const content = await response.json();
                return reject({status: response.status, id: timeTriggeredAction.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }

            return reject({status: INTERNAL_SERVER_ERROR, id: timeTriggeredAction.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteTimeTriggredAction = async (id: string): Promise<DeleteEntityResult> => {
    try {
        const url = `timeTriggeredAction/${id}`;
        const timeTriggeredAction = {
            data: {
                type: "timeTriggeredAction",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, timeTriggeredAction)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();

            return reject({status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
